import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useApp } from '../context/AppContext';

const { FiDownload, FiFileText, FiFile, FiUsers, FiFilter, FiBarChart2 } = FiIcons;

const ReportExport = ({ course, onExport, onClose }) => {
  const { state } = useApp();
  const [includeClassGroups, setIncludeClassGroups] = useState(true);
  const [selectedGroups, setSelectedGroups] = useState(['All Groups']);
  
  // Get unique class groups from students data
  const classGroups = React.useMemo(() => {
    const groups = new Set(['All Groups']);
    state.studentsData.forEach(student => {
      const group = student.classGroup || student.class_group || 'Ungrouped';
      groups.add(group);
    });
    return Array.from(groups);
  }, [state.studentsData]);
  
  const handleExport = (format) => {
    // Pass additional options to export function
    onExport(course, format, {
      includeClassGroups,
      selectedGroups: includeClassGroups ? selectedGroups : ['All Groups']
    });
    onClose();
  };

  const toggleGroupSelection = (group) => {
    if (group === 'All Groups') {
      setSelectedGroups(['All Groups']);
    } else {
      const newSelection = selectedGroups.includes(group)
        ? selectedGroups.filter(g => g !== group)
        : [...selectedGroups.filter(g => g !== 'All Groups'), group];
      
      setSelectedGroups(newSelection.length > 0 ? newSelection : ['All Groups']);
    }
  };

  const exportOptions = [
    {
      format: 'pdf',
      title: 'PDF Report',
      description: 'Complete report in PDF format for printing and sharing',
      icon: FiFileText,
      color: 'text-red-600'
    },
    {
      format: 'excel',
      title: 'Excel Spreadsheet',
      description: 'Data in Excel format for further analysis',
      icon: FiFile,
      color: 'text-green-600'
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg"
      >
        <h3 className="text-xl font-bold text-gray-900 mb-4">Export Report</h3>
        <p className="text-gray-600 mb-6">
          Choose the format to export your report for <strong>{course.courseName || course.course_name}</strong>
        </p>

        {/* New: Class Group Options */}
        <div className="mb-6 bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center">
              <SafeIcon icon={FiUsers} className="w-5 h-5 text-blue-600 mr-2" />
              <h4 className="font-medium text-blue-900">Class Group Analysis</h4>
            </div>
            <label className="inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                checked={includeClassGroups} 
                onChange={() => setIncludeClassGroups(!includeClassGroups)}
                className="sr-only peer"
              />
              <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-blue-300 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              <span className="ml-3 text-sm font-medium text-gray-600">Include</span>
            </label>
          </div>
          
          {includeClassGroups && (
            <div className="mt-3">
              <p className="text-sm text-blue-700 mb-2">Select class groups to include in the report:</p>
              <div className="flex flex-wrap gap-2">
                {classGroups.map(group => (
                  <button
                    key={group}
                    onClick={() => toggleGroupSelection(group)}
                    className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                      selectedGroups.includes(group) 
                        ? 'bg-blue-600 text-white'
                        : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                    }`}
                  >
                    {group}
                  </button>
                ))}
              </div>
              <div className="mt-2 text-xs text-blue-600">
                {selectedGroups.includes('All Groups') 
                  ? 'Generating combined report for all groups' 
                  : `Selected ${selectedGroups.length} group(s)`}
              </div>
            </div>
          )}
        </div>

        {/* Report Preview */}
        <div className="mb-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
          <div className="flex items-center mb-3">
            <SafeIcon icon={FiBarChart2} className="w-5 h-5 text-gray-600 mr-2" />
            <h4 className="font-medium text-gray-900">Report Content Preview</h4>
          </div>
          <ul className="text-sm text-gray-600 space-y-1">
            <li className="flex items-center">
              <span className="w-2 h-2 bg-blue-600 rounded-full mr-2"></span>
              Overall student performance analysis
            </li>
            <li className="flex items-center">
              <span className="w-2 h-2 bg-blue-600 rounded-full mr-2"></span>
              Comprehensive grade distribution statistics
            </li>
            {includeClassGroups && (
              <li className="flex items-center">
                <span className="w-2 h-2 bg-blue-600 rounded-full mr-2"></span>
                {selectedGroups.includes('All Groups') 
                  ? 'Combined class group analysis' 
                  : `Analysis for ${selectedGroups.length} selected group(s)`}
              </li>
            )}
            <li className="flex items-center">
              <span className="w-2 h-2 bg-blue-600 rounded-full mr-2"></span>
              Detailed student marks by assessment
            </li>
            <li className="flex items-center">
              <span className="w-2 h-2 bg-blue-600 rounded-full mr-2"></span>
              CLO and PLO achievement metrics
            </li>
          </ul>
        </div>

        <div className="space-y-3">
          {exportOptions.map((option) => (
            <button
              key={option.format}
              onClick={() => handleExport(option.format)}
              className="w-full p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors text-left"
            >
              <div className="flex items-center space-x-3">
                <SafeIcon icon={option.icon} className={`w-6 h-6 ${option.color}`} />
                <div>
                  <h4 className="font-medium text-gray-900">{option.title}</h4>
                  <p className="text-sm text-gray-600">{option.description}</p>
                </div>
              </div>
            </button>
          ))}
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ReportExport;